import { getDashboardGetByKey, postDashboardSaveDashboardUserSettings } from '@apis/Customers';
import { DashboardConfig, DashboardUserSettings } from '@apis/Customers/model';
import {
    Box,
    Button,
    Card,
    CloseButton,
    Collapse,
    Divider,
    Drawer,
    Group,
    LoadingOverlay,
    Space,
    Switch,
    Text,
    TextInput,
    ThemeIcon,
    Title,
    useMantineTheme,
} from '@mantine/core';
import { useInputState } from '@mantine/hooks';
import { PanelBody, PanelContent } from '@root/Design/Layout';
import { EventEmitter, useEventValue } from '@root/Services/EventEmitter';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Check } from 'tabler-icons-react';
import { IDashboardConfigBase } from './DashboardPersistenceService';
import { DashboardConfigLoadPanel } from './DashboardConfigLoadPanel';
import { FullSwitchCard } from '@root/Design/Switches';
import { useRouteBoundPortal } from '../Router/RouteBoundPortal';

interface DashboardConfigPanelProps {
    configKey: string;
    opened: boolean;
    onClose: () => void;
    title: string;
    saveTitle: string;
    savePrompt: string;
    loadTitle: string;
    loadPrompt: string;
    tabText?: { my?: string; shared?: string; default?: 'My Saved Views' | 'Shared Saved Views' };
    userSettings: DashboardUserSettings;
    onSettingsChange?: (settings: DashboardUserSettings) => void;
    onSave: (name: string) => Promise<void>;
    onLoad: (config: { id: number; layout: IDashboardConfigBase }) => void;
    onConfigChange: (config: { id: number; layout: IDashboardConfigBase }, action: 'rename' | 'delete') => void;
    canSave?: boolean;
    hideAutosaveOption?: boolean;
}
export function DashboardConfigPanel(props: DashboardConfigPanelProps) {
    const updated = useMemo(() => EventEmitter.empty(), []);
    const onSave = useCallback(
        async (name: string) => {
            await props.onSave(name);
            updated.emit();
        },
        [props.onSave]
    );
    const target = useRouteBoundPortal();
    return (
        <>
            <Drawer
                size={500}
                target={target}
                opened={props.opened}
                onClose={props.onClose}
                position="right"
                withCloseButton={false}
                withinPortal
                overlayOpacity={0.25}
            >
                {props.opened && (
                    <PanelContent>
                        <Group position="apart" px="xl" py="sm">
                            <Title order={4}>{props.title}</Title>
                            <CloseButton size="lg" onClick={props.onClose} data-atid="DashConfigDrawerCloseButton" />
                        </Group>
                        <Divider color="gray.2" />
                        {props.canSave === false ? null : (
                            <>
                                <Space h="lg" />
                                <Box px="xl">
                                    <SavePanel title={props.saveTitle} prompt={props.savePrompt} onSave={onSave} />
                                </Box>
                                <Space h="xs" />
                                {props.hideAutosaveOption ? null : (
                                    <>
                                        <Box px="xl">
                                            <AutoSaveToggle userSettings={props.userSettings} onChange={props.onSettingsChange} />
                                        </Box>
                                        <Space h="lg" />
                                    </>
                                )}
                            </>
                        )}

                        <PanelBody noPadding>
                            <DashboardConfigLoadPanel
                                onConfigChange={props.onConfigChange}
                                onSelect={props.onLoad}
                                title={props.loadTitle}
                                prompt={props.loadPrompt}
                                configKey={props.configKey}
                                tabText={props.tabText}
                                updated={updated}
                            />
                        </PanelBody>
                    </PanelContent>
                )}
            </Drawer>
        </>
    );
}

function SavePanel({ title, prompt, onSave }: { title: string; prompt: string; onSave: (name: string) => Promise<void> }) {
    const theme = useMantineTheme();
    const [name, setName] = useInputState<string>('');
    const [saved, setSaved] = useState<boolean>();
    const save = useCallback(() => {
        setSaved(false);
        onSave(name).then(() => setSaved(true));
    }, [name]);
    return (
        <Card sx={{ background: saved ? theme.colors.success[2] : theme.colors.gray[2] }} withBorder radius="md">
            <LoadingOverlay visible={saved === false} />
            <Collapse in={!saved}>
                <Text>{title}</Text>
                <Text size="sm" color="dimmed">
                    {prompt}
                </Text>
                <Space h="xs" />
                <TextInput autoComplete="off" value={name} onChange={setName} data-atid="SavedViewsTextInput" />
                <Space h="xs" />
                <Group position="right">
                    <Button disabled={!name} variant="outline" onClick={save} data-atid="SaveViewButton">
                        Save
                    </Button>
                </Group>
            </Collapse>
            <Collapse in={!!saved}>
                <Group>
                    <Check color={theme.colors.success[6]} />
                    <Text color="success">Saved</Text>
                </Group>
            </Collapse>
        </Card>
    );
}

function AutoSaveToggle({ userSettings, onChange }: { userSettings: DashboardUserSettings; onChange?: (settings: DashboardUserSettings) => void }) {
    const [checked, setChecked] = useState(!!userSettings.DashboardAutoSave);
    const toggle = async () => {
        userSettings.DashboardAutoSave = !userSettings.DashboardAutoSave;
        setChecked(userSettings.DashboardAutoSave);
        userSettings = await postDashboardSaveDashboardUserSettings(userSettings);
        onChange?.(userSettings);
    };
    return (
        <FullSwitchCard
            checked={checked}
            onClick={toggle}
            label={`Autosave ${checked ? 'enabled' : 'disabled'}`}
            tooltip="Toggle automatic saving based on grid or dashboard changes"
        />
    );
}
